import { useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';


export default function ForcePasswordChange() {
  const { checkAuth, logout } = useAuth();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error('New password and confirm password do not match');
      return;
    }
    if (form.new_password.length < 4) {
      toast.error('New password must be at least 4 characters');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/api/auth/change-password`, {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success('Password changed successfully! Redirecting...');
      // Re-fetch user profile which now has force_password_change: false
      await checkAuth();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-zinc-300 shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="w-12 h-12 bg-orange-500 rounded-sm flex items-center justify-center text-white font-black text-lg mx-auto mb-4">K</div>
          <CardTitle className="text-2xl font-bold text-zinc-950">Password Change Required</CardTitle>
          <p className="text-sm text-zinc-500 mt-2">Admin has requested you change your password before continuing.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Current Password *</Label>
              <Input
                data-testid="force-change-current"
                type="password"
                required
                value={form.current_password}
                onChange={(e) => setForm(prev => ({ ...prev, current_password: e.target.value }))}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">New Password *</Label>
              <Input
                data-testid="force-change-new"
                type="password"
                required
                value={form.new_password}
                onChange={(e) => setForm(prev => ({ ...prev, new_password: e.target.value }))}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-bold uppercase tracking-[0.2em] text-zinc-500">Confirm New Password *</Label>
              <Input
                data-testid="force-change-confirm"
                type="password"
                required
                value={form.confirm_password}
                onChange={(e) => setForm(prev => ({ ...prev, confirm_password: e.target.value }))}
                className="h-11"
              />
            </div>
            <Button
              type="submit"
              data-testid="force-change-submit"
              disabled={submitting}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white h-11 font-bold"
            >
              {submitting ? 'Changing Password...' : 'Change Password & Continue'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              data-testid="force-change-logout"
              onClick={logout}
              className="w-full text-zinc-500 hover:text-zinc-700"
            >
              Logout Instead
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
